import { useEffect, useRef } from 'react';
import { Square, Wifi, WifiOff, TerminalSquare } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Badge, type BadgeTone } from '@/shared/components/ui/Badge';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { cn } from '@/shared/utils/utils';
import type { ScrapeStreamState } from '../hooks/useScrapeStream';
import type { StreamStatus } from '../types';

interface LiveSessionProps {
  stream: ScrapeStreamState & { isActive: boolean };
  onStop: () => void;
  maxResults?: number;
}

const STATUS_TONE: Record<StreamStatus, BadgeTone> = {
  idle: 'neutral',
  connecting: 'info',
  running: 'info',
  completed: 'success',
  stopped: 'warning',
  error: 'danger',
};

/** Terminal line color + glyph per log status. */
const LOG_STYLE: Record<string, { color: string; glyph: string }> = {
  info: { color: 'text-slate-300', glyph: '›' },
  success: { color: 'text-emerald-400', glyph: '✓' },
  warning: { color: 'text-amber-400', glyph: '!' },
  error: { color: 'text-rose-400', glyph: '✗' },
};

function fmtElapsed(sec: number) {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function LiveSession({ stream, onStop, maxResults }: LiveSessionProps) {
  const { status, message, logs, leads, savedCount, dupCount, elapsedSec, isActive } = stream;
  const pct = maxResults && maxResults > 0 ? Math.min(100, Math.round((savedCount / maxResults) * 100)) : 0;

  // Auto-scroll the terminal to the newest line.
  const termRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    termRef.current?.scrollTo({ top: termRef.current.scrollHeight });
  }, [logs.length, leads.length, status]);

  const completedEmpty = status === 'completed' && savedCount === 0;

  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            {isActive ? <Wifi className="h-4 w-4 text-success" /> : <WifiOff className="h-4 w-4 text-on-surface-variant" />}
            Live Session
          </span>
        }
        action={
          isActive ? (
            <Button variant="danger" size="sm" onClick={onStop}>
              <Square className="h-3.5 w-3.5" /> Stop
            </Button>
          ) : (
            <Badge tone={STATUS_TONE[status]}>{status}</Badge>
          )
        }
      />
      <CardBody className="space-y-4">
        {status === 'idle' ? (
          <EmptyState title="No active scan" description="Configure and start a scan to watch the live console here." />
        ) : (
          <>
            {/* Metrics */}
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: 'Saved', value: savedCount, tone: 'text-success' },
                { label: 'Duplicates', value: dupCount, tone: 'text-warning' },
                { label: 'Elapsed', value: fmtElapsed(elapsedSec), tone: 'text-on-surface' },
                { label: 'Status', value: status, tone: 'text-on-surface' },
              ].map((m) => (
                <div key={m.label} className="rounded-card border border-outline bg-surface p-3 text-center">
                  <p className={cn('text-data-lg font-semibold', m.tone)}>{m.value}</p>
                  <p className="mt-0.5 text-label-caps uppercase text-on-surface-variant">{m.label}</p>
                </div>
              ))}
            </div>

            {/* Progress */}
            {maxResults && maxResults > 0 && (
              <div>
                <div className="mb-1 flex justify-between text-body-sm text-on-surface-variant">
                  <span>Progress</span>
                  <span>{savedCount}/{maxResults}</span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-surface-variant">
                  <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
            )}

            {completedEmpty && (
              <p className="rounded-card border border-info/30 bg-info/10 px-3 py-2 text-body-sm text-on-surface">
                Scan finished — no new leads saved. All discovered businesses were already in your CRM (duplicates skipped). Try a different niche or location.
              </p>
            )}

            {/* ── Interactive terminal ───────────────────────────────────── */}
            <div className="overflow-hidden rounded-card border border-[#1b2433] bg-[#0a0e16] shadow-surface-2">
              {/* window chrome */}
              <div className="flex items-center gap-2 border-b border-[#1b2433] bg-[#0d121c] px-3 py-2">
                <span className="flex gap-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-rose-500/80" />
                  <span className="h-2.5 w-2.5 rounded-full bg-amber-400/80" />
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-500/80" />
                </span>
                <span className="ml-1 flex items-center gap-1.5 font-mono text-[11px] text-slate-400">
                  <TerminalSquare className="h-3.5 w-3.5" /> zynx@scraper: ~/scan
                </span>
                {isActive && (
                  <span className="ml-auto flex items-center gap-1.5 font-mono text-[11px] text-emerald-400">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" /> live
                  </span>
                )}
              </div>

              {/* log stream */}
              <div
                ref={termRef}
                className="relative h-64 overflow-y-auto px-3 py-2.5 font-mono text-[12px] leading-relaxed"
                style={{ scrollbarWidth: 'thin' }}
              >
                {/* boot line */}
                <div className="text-slate-500">
                  <span className="text-emerald-400">$</span> zynx scrape --start
                </div>
                {logs.length === 0 && !isActive ? (
                  <div className="text-slate-600">Waiting for activity…</div>
                ) : (
                  logs.map((log) => {
                    const st = LOG_STYLE[log.status] ?? LOG_STYLE.info;
                    return (
                      <div key={log.id} className={cn('flex gap-2 whitespace-pre-wrap break-words', st.color)}>
                        <span className="select-none opacity-60">{st.glyph}</span>
                        <span>{log.message}</span>
                      </div>
                    );
                  })
                )}

                {/* streamed discoveries */}
                {leads.slice(0, 8).map((l) => (
                  <div key={`lead-${l.id}`} className="flex gap-2 text-emerald-300">
                    <span className="select-none opacity-70">+</span>
                    <span>
                      FOUND {l.name}
                      {l.phone ? ` · ${l.phone}` : ''}
                    </span>
                  </div>
                ))}

                {/* live cursor / status line */}
                {isActive ? (
                  <div className="flex items-center gap-2 text-emerald-400">
                    <span className="opacity-70">▸</span>
                    <span className="text-slate-400">{message || 'scanning'}</span>
                    <span className="inline-block h-3.5 w-2 animate-pulse bg-emerald-400" />
                  </div>
                ) : (
                  <div className="text-slate-500">
                    <span className="text-emerald-400">$</span> {status === 'completed' ? 'done.' : status === 'error' ? 'aborted.' : status}
                  </div>
                )}
              </div>
            </div>

            {/* Discovered list (only when there are saved leads) */}
            {leads.length > 0 && (
              <div>
                <h3 className="mb-1 text-label-caps uppercase text-on-surface-variant">Discovered ({leads.length})</h3>
                <div className="max-h-40 space-y-1 overflow-y-auto">
                  {leads.map((l) => (
                    <div key={l.id} className="rounded-DEFAULT border border-outline bg-surface px-3 py-1.5 text-data-md text-on-surface">
                      <span className="font-medium">{l.name}</span>
                      {l.phone && <span className="ml-2 text-body-sm text-on-surface-variant">{l.phone}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {message && !isActive && !completedEmpty && (
              <p className={cn('text-body-sm', status === 'error' ? 'text-danger' : 'text-on-surface-variant')}>{message}</p>
            )}
          </>
        )}
      </CardBody>
    </Card>
  );
}
